import React from "react";
import { Pencil, ShieldAlert, ShieldCheck } from "lucide-react";
import { metaFor } from "@/lib/deviceMeta";

export default function DeviceCard({ device, active, onClick, isNew, label, onRename, flagged, onToggleFlag }) {
  const meta = metaFor(device.device_type);
  const Icon = meta.icon;
  return (
    <div
      data-testid={`device-card-${device.ip}`}
      onClick={() => onClick(device)}
      role="button"
      dir="rtl"
      className={`group relative w-full cursor-pointer rounded-xl border p-3 text-right transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 ${
        flagged
          ? "border-rose-400/70 bg-rose-50/70 shadow-sm"
          : isNew
          ? "border-amber-400/60 bg-amber-50/70 shadow-sm"
          : active
          ? "border-[#0055FF]/40 bg-[#0055FF]/[0.06] shadow-sm"
          : "border-black/5 bg-white/70 hover:bg-white"
      }`}
    >
      <div className="absolute left-2 top-2 z-10 flex gap-1">
        <button
          data-testid={`flag-btn-${device.ip}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFlag && onToggleFlag(device);
          }}
          title={flagged ? "הסר סימון חשוד" : "סמן כחשוד"}
          className={`rounded-md p-1 transition-[opacity,color,background-color] hover:bg-black/5 ${
            flagged
              ? "text-rose-500 opacity-100"
              : "text-[#94A3B8] opacity-0 hover:text-rose-500 group-hover:opacity-100"
          }`}
        >
          {flagged ? <ShieldCheck size={13} /> : <ShieldAlert size={13} />}
        </button>
        <button
          data-testid={`rename-btn-${device.ip}`}
          onClick={(e) => {
            e.stopPropagation();
            onRename && onRename(device);
          }}
          title="שנה שם"
          className="rounded-md p-1 text-[#94A3B8] opacity-0 transition-[opacity,color,background-color] hover:bg-black/5 hover:text-[#0055FF] group-hover:opacity-100"
        >
          <Pencil size={13} />
        </button>
      </div>
      <div className="flex items-center gap-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: meta.color + "1A", color: meta.color }}
        >
          <Icon size={18} strokeWidth={2.2} />
        </span>
        <div className="min-w-0 flex-1">
          {label && (
            <div className="truncate text-[13px] font-bold text-[#0A0A0A]">{label}</div>
          )}
          <div className="flex items-center justify-between gap-2">
            <span
              className={`font-mono font-semibold text-[#0A0A0A] ${label ? "text-[11px] text-[#52525B]" : "text-[13px]"}`}
              dir="ltr"
            >
              {device.ip}
            </span>
            <span className="shrink-0 text-[11px] font-medium text-[#52525B]">
              {meta.label}
            </span>
          </div>
          {!label && device.hostname && (
            <div className="truncate text-[11px] text-[#52525B]" dir="ltr">
              {device.hostname}
            </div>
          )}
          {device.subnet && (
            <div className="truncate font-mono text-[9px] text-[#94A3B8]" dir="ltr">
              רשת {device.subnet}
            </div>
          )}
          {device.mac && (
            <div className="truncate font-mono text-[10px] text-[#94A3B8]" dir="ltr">
              {device.mac}
              {device.vendor ? ` · ${device.vendor}` : ""}
            </div>
          )}
        </div>
      </div>
      {(device.services?.length > 0) && (
        <div className="mt-2 flex flex-wrap gap-1" dir="ltr">
          {device.services.slice(0, 5).map((s) => (
            <span
              key={s}
              className="rounded-md bg-black/[0.04] px-1.5 py-0.5 font-mono text-[9px] font-medium text-[#52525B]"
            >
              {s}
            </span>
          ))}
          {device.services.length > 5 && (
            <span className="rounded-md bg-black/[0.04] px-1.5 py-0.5 text-[9px] text-[#94A3B8]">
              +{device.services.length - 5}
            </span>
          )}
        </div>
      )}
      {(device.is_router || device.is_self || isNew || flagged) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {flagged && (
            <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[9px] font-semibold text-rose-600">
              חשוד
            </span>
          )}
          {isNew && (
            <span className="rounded-full bg-amber-400/20 px-2 py-0.5 text-[9px] font-semibold text-amber-600">
              חדש
            </span>
          )}
          {device.is_router && (
            <span className="rounded-full bg-[#0055FF]/10 px-2 py-0.5 text-[9px] font-semibold text-[#0055FF]">
              נתב
            </span>
          )}
          {device.is_self && (
            <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[9px] font-semibold text-emerald-600">
              המכשיר שלי
            </span>
          )}
        </div>
      )}
    </div>
  );
}
